import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { ChevronDownIcon, ChevronUpIcon } from 'components/v2/epd-compiler-inputs/common-icons';

const StyledHelpBox = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 0.5rem;
  text-align: start;
  padding: 0.5rem 1rem;
  background-color: ${(props) => props.theme.colors.elementsFilledInput};
  border-radius: 4px;
  ${(props) => props.theme.typography.bodyBody14Regular};
`;

const Inner = styled.div<{ $clamp: boolean }>`
  flex: 1;
  min-width: 0;
  word-break: break-word;
  ${(p) =>
    p.$clamp
      ? `
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `
      : ''}
`;

const ToggleButton = styled.button`
  flex-shrink: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 2px;
  margin: 0;
  line-height: 0;
  align-self: flex-start;
  margin-top: 2px;
  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.primaryOrange};
    outline-offset: 2px;
  }
`;

function stripHtmlForLength(html: string): string {
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export type ExpandableHelpHtmlProps = {
  html: string;
  /** Plain-text length above which expand/collapse is shown (after stripping tags). */
  collapseThreshold?: number;
  className?: string;
};

export const ExpandableHelpHtml: React.FC<ExpandableHelpHtmlProps> = ({ html, collapseThreshold = 120, className }) => {
  const plainLen = useMemo(() => stripHtmlForLength(html).length, [html]);
  const collapsible = plainLen > collapseThreshold;
  const [expanded, setExpanded] = useState(false);

  if (!collapsible) {
    return (
      <StyledHelpBox className={className} style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
        <div style={{ whiteSpace: 'pre-line' }} dangerouslySetInnerHTML={{ __html: html }} />
      </StyledHelpBox>
    );
  }

  return (
    <StyledHelpBox className={className}>
      <Inner $clamp={!expanded}>
        <div style={{ whiteSpace: 'pre-line' }} dangerouslySetInnerHTML={{ __html: html }} />
      </Inner>
      <ToggleButton
        type="button"
        aria-expanded={expanded}
        aria-label={expanded ? 'Collapse help' : 'Expand help'}
        onClick={() => setExpanded((e) => !e)}
      >
        {expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
      </ToggleButton>
    </StyledHelpBox>
  );
};
